import { useCallback, useEffect, useRef, useState } from 'react';
import { Action, AuthType, init, RuntimeFilterOp } from '@thoughtspot/visual-embed-sdk';
import { LiveboardEmbed /** SpotterEmbed */ } from '@thoughtspot/visual-embed-sdk/react';
import { Box, Paper } from '@mui/material';
import { datadogLogs } from '@datadog/browser-logs';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { getSelectedAccountsSubject, getThoughtspotToken, shouldUseThoughtSpotSSO } from '../../../store/User';
import { TextBlock } from '../../Atoms/Text';
import { Icon } from '../../Atoms/Icon';
import { defaultFeatureFlags, getFeatureFlagSubject } from '../../../Api/useFeatureFlags';

type ThoughtSpotFilterItem = {
  columnName: string;
  operator: RuntimeFilterOp;
  values: string[];
};

const VISIBLE_ACTIONS = [
  Action.Pin,
  Action.DrillDown,
  Action.AddFilter,
  Action.AxisMenuFilter,
  Action.ConfigureFilter,
  Action.Explore,
  Action.Download,
  Action.DownloadAsCsv,
  Action.DownloadAsPdf,
  Action.DownloadAsPng,
  Action.DownloadLiveboard,
  Action.DownloadLiveboardAsContinuousPDF
];

export const LiveboardWrapper = () => {
  const TS_HOST = 'https://upstack.thoughtspot.cloud';
  const AUTH_TYPE = AuthType.None;
  const [initialized, setInitialized] = useState(false);
  const [invalidUser, setInvalidUser] = useState(false);
  const [runTimeFilters, setRunTimeFilters] = useState<ThoughtSpotFilterItem[]>([]);
  const [liveboardId, setLiveboardId] = useState(defaultFeatureFlags.ts_liveboard_id || '');
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const filtersRef = useRef<ThoughtSpotFilterItem[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const featureFlagsSub = getFeatureFlagSubject().subscribe((flags) => {
      const newId = flags.ts_liveboard_id || '';
      setLiveboardId((prev) => (prev === newId ? prev : newId));
    });
    const selectedAccountSub = getSelectedAccountsSubject().subscribe((accounts) => {
      if (Array.isArray(accounts)) {
        const newValues = accounts
          .map((a) => a.id)
          .filter((a): a is string => Boolean(a))
          .sort();
        const currentValues = filtersRef.current[0]?.values ?? [];
        if (currentValues.length === newValues.length && currentValues.every((v, i) => v === newValues[i])) {
          return;
        }
        const filters: ThoughtSpotFilterItem[] = [
          {
            columnName: 'ASSET_ACCOUNTID_UPSTACK_ID',
            operator: RuntimeFilterOp.IN,
            values: newValues
          }
        ];
        filtersRef.current = filters;
        setRunTimeFilters(filters);
      }
    });

    const getUserToken = async () => {
      const loginInfo = await getThoughtspotToken();
      return loginInfo?.token || '';
    };

    const initializeTrustedAuthTokenCookieless = async () => {
      const loginInfo = await getThoughtspotToken();
      if (!loginInfo) {
        setInvalidUser(true);
        return;
      }
      const { username } = loginInfo;
      init({
        thoughtSpotHost: TS_HOST,
        authType: AuthType.TrustedAuthTokenCookieless,
        username,
        getAuthToken: getUserToken
      });
      setInitialized(true);
    };

    if (shouldUseThoughtSpotSSO()) {
      init({
        thoughtSpotHost: TS_HOST,
        authType: AUTH_TYPE
      });
      setInitialized(true);
    } else {
      initializeTrustedAuthTokenCookieless();
    }

    return () => {
      if (selectedAccountSub) selectedAccountSub.unsubscribe();
      if (featureFlagsSub) featureFlagsSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setContainerHeight(Math.floor(entry.contentRect.height));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [initialized]);

  const handleLoad = useCallback(
    (a: unknown) => datadogLogs.logger.info('ThoughtSpot Embed Loaded', { payload: a }),
    []
  );
  const resolvedLiveboardId = liveboardId || 'b80b9797-1df1-4b40-8c87-4eef80ce9c46';
  const frameHeight = containerHeight > 0 ? `${containerHeight}px` : '800px';

  return (
    <>
      {!initialized && !invalidUser && <ComponentSpinner />}
      {!initialized && invalidUser && (
        <Box sx={{ px: 36, py: 4, textAlign: 'center' }}>
          <Paper sx={{ p: 4 }}>
            <div className="flex items-center gap-2 mb-2 justify-center">
              <Icon type="alert" />
              <TextBlock
                size="lg"
                weight="bold"
                color="orange">
                ThoughtSpot User Not Found
              </TextBlock>
            </div>
            <p>Your ThoughtSpot user could not be found. Please contact support.</p>
          </Paper>
        </Box>
      )}
      {initialized && (
        <div
          ref={containerRef}
          className="relative flex-1 min-h-0">
          {/* <SpotterEmbed
            frameParams={{ height: frameHeight, width: '100%' }}
            worksheetId='f28c289b-bab4-4435-bf7e-09b8b99dbd79'
            runtimeFilters={runTimeFilters}
          /> */}
          <LiveboardEmbed
            frameParams={{ height: frameHeight, width: '100%' }}
            className="liveboard-embed"
            runtimeFilters={runTimeFilters}
            visibleActions={VISIBLE_ACTIONS}
            liveboardId={resolvedLiveboardId}
            onLoad={handleLoad}
            onError={(e) => datadogLogs.logger.error('ThoughtSpot Embed Error', e)}
          />
          {/** The below is used to remove the TS branding */}
          <div className="fixed bottom-0 w-[100%] h-[20px] bg-main-300" />
        </div>
      )}
    </>
  );
};
